import type { ReportLayoutConfig } from '@/features/reports'

// v3 `configs/Audience/UserBehavior.js` (97 ln) — retyped, with defect D28 (analysis 24
// §D28, extending contract 24 §3.3 #9) fixed in the port. v3's page never rendered from this
// config (`views/App/Audience/UserBehavior.vue` hardcodes its 10 panels), so three sections
// carry schedule-tab bindings that exist NOWHERE else in v3 `src/` — copy-paste phantoms. The
// standing rule (port the design, never the defect) means every binding below is re-derived
// from the v3 PANEL that renders the section; the proving consumer is cited per section.
//
// D28 fixes (phantom → re-derived):
// - `:12` `user_behavior_over_view`  → `user_behavior_visit_by_time`  (UserBehaviorOverview.vue:61,82)
// - `:84` `user_behavior_landing_page` → `metrics_mp_landingpage`     (UserBehaviorPerformanceByPages.vue:19,34)
// - `:93` `user_behavior_landing_page` → NO schedule tab — the cohort panel bypasses the
//   schedule and hardcodes `partition_name: 'cohort_report_data'` (UserBehaviorChart.vue:84)
// Contract §3.3 #9's copy-paste inner-tab NAMES are corrected too: `:84` was 'RFM Segments',
// `:93` was 'user_behavior__session_cohort'.
//
// ORP-18911 renders the first nine bespoke panels through UserBehaviorPage's typed ReportPage
// slot; ORP-18912 adds Session Cohort through the same preview host. Anchor ids are kept verbatim
// (deep links + customize partitions). Beware for the panel port: v3 SWAPPED two DOM
// anchors — `UserBehaviorPerformanceByPages.vue:3` renders `user_behavior__performance_by_devices`
// and `UserBehaviorPerformanceByDevices.vue:3` renders `user_behavior__performance_by_pages`;
// the ids here follow the section TITLES (as the v3 config already did), not the swapped DOM.
// v3 `importance`/`search_value` blobs are dropped — search-index metadata with no v4
// section-level home (same decision as every 08 seed and module 27's configs; module 15 pending).

const USER_BEHAVIOR_REPORT_ID = 28
/** Key Demographics alone reads the Demographic Analysis report (v3 `UserBehavior.js:26`). */
const DEMOGRAPHICS_REPORT_ID = 70
/** Performance by Pages reads Landing Page report 22, never User Behavior report 28 (D29). */
const LANDING_PAGE_REPORT_ID = 22

export const USER_BEHAVIOR: ReportLayoutConfig = [
  {
    name: 'Overview',
    labelKey: 'audience.userBehavior.tabs.overview',
    sections: [
      {
        id: 'user_behavior__behavioral_trends_and_insights',
        titleKey: 'audience.userBehavior.sections.behavioralTrends',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // D28: v3 bound the phantom `user_behavior_over_view`; the panel gates and requests
        // through `user_behavior_visit_by_time` (UserBehaviorOverview.vue:61,82).
        tabs: [{ name: 'Behavioral Trends and Insights', tabs: ['user_behavior_visit_by_time'] }],
      },
      {
        id: 'user_behavior__top_locations',
        titleKey: 'audience.userBehavior.sections.topLocations',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorWorldMap.vue:73.
        tabs: [{ name: 'Top Locations', tabs: ['user_behavior_city_device'] }],
      },
      {
        id: 'user_behavior__key_demographics',
        titleKey: 'audience.userBehavior.sections.keyDemographics',
        reportId: DEMOGRAPHICS_REPORT_ID,
        // Consumer: UserBehaviorAgeGenderDemographics.vue:43.
        tabs: [{ name: 'Key Demographics', tabs: ['metrics_mp_demographic_age_gender'] }],
      },
      {
        id: 'user_behavior__top_session_windows',
        titleKey: 'audience.userBehavior.sections.topSessionWindows',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorVisitHeatMap.vue:66.
        tabs: [{ name: 'Top Session Windows', tabs: ['user_behavior_visit_by_time'] }],
      },
      {
        id: 'user_behavior__top_devices_categories',
        titleKey: 'audience.userBehavior.sections.topDevicesCategories',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorTopDevices.vue:49.
        tabs: [{ name: 'Top Devices Categories', tabs: ['user_behavior_city_device'] }],
      },
      {
        id: 'user_behavior__top_channels',
        titleKey: 'audience.userBehavior.sections.topChannels',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorTopChannels.vue:95.
        tabs: [{ name: 'Top Channels', tabs: ['user_behavior_top_channels'] }],
      },
      {
        id: 'user_behavior__performance_by_devices',
        titleKey: 'audience.userBehavior.sections.performanceByDevices',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorPerformanceByDevices.vue:20,50.
        tabs: [{ name: 'Performance by Devices', tabs: ['user_behavior_sales_by_technology'] }],
      },
      {
        id: 'user_behavior__trending_queries_by_platform',
        titleKey: 'audience.userBehavior.sections.trendingQueriesByPlatform',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // Consumer: UserBehaviorTrendingQueries.vue:18,35.
        tabs: [{ name: 'Trending Queries by Platform', tabs: ['user_behavior_search_terms'] }],
      },
      {
        id: 'user_behavior__performance_by_pages',
        titleKey: 'audience.userBehavior.sections.performanceByPages',
        reportId: LANDING_PAGE_REPORT_ID,
        // D28: v3 bound the phantom `user_behavior_landing_page` under the copy-paste tab name
        // 'RFM Segments'; the panel binds `metrics_mp_landingpage`
        // (UserBehaviorPerformanceByPages.vue:19,34 — the same tab LandingPage.js:21 shares).
        tabs: [{ name: 'Performance by Pages', tabs: ['metrics_mp_landingpage'] }],
      },
      {
        id: 'user_behavior__session_cohort',
        titleKey: 'audience.userBehavior.sections.sessionCohort',
        reportId: USER_BEHAVIOR_REPORT_ID,
        // D28: v3 bound the phantom `user_behavior_landing_page` here too. The cohort panel
        // consults NO schedule tab — it hardcodes `partition_name: 'cohort_report_data'`
        // (UserBehaviorChart.vue:84) and self-prunes platforms with no data — so this section
        // deliberately carries no schedule-tab binding.
        tabs: [{ name: 'Session Cohort' }],
      },
    ],
  },
]
